import { Injectable } from '@angular/core';
import { Invoice } from 'src/app/types/Invoice';
import { InvoiceStatus } from 'src/app/types/InvoiceStatus';
import { InvoiceTopLevelNumbers } from 'src/app/types/InvoiceTopLevelNumbers';
import { InvoiceLog } from 'src/app/types/invoiceLog';
import { PayoutListingFilters } from 'src/app/types/requests/PayoutListingFilters';
import { DayWisePayoutSummary } from '../../../types/DayWisePayoutSummary';
import { PaymentMethodDetails } from '../../../types/PaymentDetails';
import { Payout } from '../../../types/Payout';
import {
  PayoutFrequencyPreferences,
  PayoutsListingData,
} from '../../../types/PayoutsListingData';
import { InvoicesListingFilters } from '../../../types/requests/InvoicesListingFilters';
import { PayoutPreferencesRequest } from '../../../types/requests/PayoutPreferencesRequest';
import {
  PayoutsListingRequest,
  PayoutsListingRequestType,
} from '../../../types/requests/PayoutsListingRequest';
import { WithdrawlAgainstInvoicesRequest } from '../../../types/requests/WithdrawlAgainstInvoicesRequest';
import { WithdrawlAgainstInvoicesResponse } from '../../../types/responses/WithdrawlAgainstInvoicesResponse';
import { CoreService } from '../../core.service';
import { IInvoiceManager } from '../IInvoiceManager';
import {
  invoicetTopLevelNumbers,
  mockDayWisePayoutsSummary,
  mockInvoiceLog,
  mockInvoices,
  mockPayoutFrequencyPreferencesDaily,
  mockPayouts,
} from './mock-data';

@Injectable({
  providedIn: 'root',
})
export class MockInvoiceManagerService implements IInvoiceManager {
  constructor(private _coreService: CoreService) {}

  invoices: Invoice[] = mockInvoices;
  payouts: Payout[] = mockPayouts;
  dayWisePayoutsSummary: DayWisePayoutSummary[] = mockDayWisePayoutsSummary;
  payoutsFrequencyPreferences: PayoutFrequencyPreferences =
    mockPayoutFrequencyPreferencesDaily;

  /*   async getCustomers(): Promise<Customer[]> {
    return this._coreService.delay(mockCustometrs);
  } */

  /*   async getInvoices(): Promise<Invoice[]> {
    console.log('Fetching invoices');
    return this._coreService.delay(this.invoices);
  } */

  updateLogoId(logoFileId: string): Promise<boolean> {
    return this._coreService.delayResolve(true);
  }

  async getInvoiceById(): Promise<Invoice> {
    //getInvoicebyID
    return this._coreService.delayResolve(this.invoices[0]);
  }

  async getInvoiceByPayoutId(): Promise<Invoice> {
    return this._coreService.delayResolve(this.invoices[0]);
  }

  async getBankAccountByPayoutId(
    payoutId: string,
  ): Promise<PaymentMethodDetails> {
    const mockBankAccount: PaymentMethodDetails = {
      accountName: 'Mock Account Name',
      accountNumber: '1234567890',
      accountType: 'Savings',
      ifscCode: 'MOCK0001234',
      bankName: 'Mock Bank',
      bankAddress: 'Mock Bank Branch',
      paymentMethodType: 'INDIAN_BANK_ACCOUNT',
      achRouting: '',
    };
    return this._coreService.delayResolve(mockBankAccount);
  }

  async createInvoice(invoice: Partial<Invoice>): Promise<string> {
    return this._coreService.delayResolve('INV-001');
  }

  async createInvoicePreview(
    invoice: Partial<Invoice>,
  ): Promise<{ success: boolean; data: Blob; error: string }> {
    // Create a mock PDF blob for testing
    const mockPdfContent = `%PDF-1.4
1 0 obj
<<
/Type /Catalog
/Pages 2 0 R
>>
endobj
2 0 obj
<<
/Type /Pages
/Kids [3 0 R]
/Count 1
>>
endobj
3 0 obj
<<
/Type /Page
/Parent 2 0 R
/MediaBox [0 0 612 792]
/Contents 4 0 R
>>
endobj
4 0 obj
<<
/Length 44
>>
stream
BT
/F1 12 Tf
72 720 Td
(Invoice Preview) Tj
ET
endstream
endobj
xref
0 5
0000000000 65535 f
0000000009 00000 n
0000000058 00000 n
0000000115 00000 n
0000000206 00000 n
trailer
<<
/Size 5
/Root 1 0 R
>>
startxref
300
%%EOF`;

    const mockBlob = new Blob([mockPdfContent], { type: 'application/pdf' });

    return this._coreService.delayResolve({
      success: true,
      data: mockBlob,
      error: '',
    });
  }

  async deleteInvoice(invoiceNo: string): Promise<boolean> {
    console.log('Deleting invoice', invoiceNo);
    const index = this.invoices.findIndex((inv) => inv.invoiceNo === invoiceNo);
    if (index !== -1) {
      this.invoices.splice(index, 1);
      return this._coreService.delayResolve(true);
    }
    return this._coreService.delayResolve(false);
  }

  async updateInvoice(invoiceNo: string): Promise<Invoice | null> {
    console.log('Updating invoice', invoiceNo);
    const invoice = this.invoices.find((inv) => inv.invoiceNo === invoiceNo);
    if (invoice) {
      return this._coreService.delayResolve(invoice);
    }
    return this._coreService.delayResolve(null);
  }

  async sendAReminderForInvoice(invoiceNo: string): Promise<boolean> {
    console.log('Sending reminder for invoice', invoiceNo);
    return this._coreService.delayResolve(true);
  }

  async sendOverdueRemindersForInvoices(
    invoiceIds: string[],
  ): Promise<boolean> {
    console.log('Sending overdue reminders for invoices', invoiceIds);
    return this._coreService.delayResolve(true);
  }

  async revokeInvoice(invoiceNo: string): Promise<boolean> {
    console.log('Revoking invoice', invoiceNo);
    const invoice = this.invoices.find((inv) => inv.invoiceNo === invoiceNo);
    if (invoice) {
      invoice.status = InvoiceStatus.Draft;
      return this._coreService.delayResolve(true);
    }
    return this._coreService.delayResolve(false);
  }

  async updateInvoiceStatus(
    invoiceNo: string,
    status: InvoiceStatus,
  ): Promise<Invoice | null> {
    console.log('Updating invoice status', invoiceNo, status);
    const invoice = this.invoices.find((inv) => inv.invoiceNo === invoiceNo);
    if (invoice) {
      invoice.status = status;
      return this._coreService.delayResolve(invoice);
    }
    return this._coreService.delayResolve(null);
  }

  async duplicateInvoice(invoiceNo: string): Promise<Invoice | null> {
    console.log('Duplicating invoice', invoiceNo);
    const invoice = this.invoices.find((inv) => inv.invoiceNo === invoiceNo);
    if (invoice) {
      const duplicate = { ...invoice, invoiceNo: `DUP-${Date.now()}` };
      this.invoices.push(duplicate);
      return this._coreService.delayResolve(duplicate);
    }
    return this._coreService.delayResolve(null);
  }

  async updateInvoiceDueDate(
    invoiceNo: string,
    newDueDate: string,
  ): Promise<Invoice | null> {
    console.log('Updating invoice due date', invoiceNo, newDueDate);
    const invoice = this.invoices.find((inv) => inv.invoiceNo === invoiceNo);
    if (invoice) {
      invoice.dueDate = newDueDate;
      return this._coreService.delayResolve(invoice);
    }
    return this._coreService.delayResolve(null);
  }

  async getInvoiceTopLevelsNumbers(): Promise<InvoiceTopLevelNumbers> {
    return this._coreService.delayResolve(invoicetTopLevelNumbers);
  }

  async getInvoiceLog(invoiceNo: string): Promise<InvoiceLog | null> {
    console.log('Invoice log ', invoiceNo);
    if (invoiceNo) {
      return this._coreService.delayResolve(mockInvoiceLog);
    }
    return this._coreService.delayResolve(null);
  }

  getPayouts(
    payoutsListingRequest: PayoutsListingRequest,
  ): Promise<PayoutsListingData> {
    let mockResponse = {} as PayoutsListingData;
    if (
      payoutsListingRequest.requestType.includes(
        PayoutsListingRequestType.DAY_WISE_PAYOUT_SUMMARY,
      )
    ) {
      mockResponse.dayWisePayoutSummary = this.dayWisePayoutsSummary;
    }
    if (
      payoutsListingRequest.requestType.includes(
        PayoutsListingRequestType.PAYOUTS_LISTING,
      )
    ) {
      mockResponse.payouts = this.payouts;
    }
    if (
      payoutsListingRequest.requestType.includes(
        PayoutsListingRequestType.CURRENT_PAYOUTS_PREFERENCES,
      )
    ) {
      mockResponse.payoutFrequencyPreferences =
        this.payoutsFrequencyPreferences;
    }
    return this._coreService.delayResolve(mockResponse);
  }

  getPayoutFIRC(fileId: string): Promise<Blob> {
    return this._coreService.delayResolve(new Blob());
  }

  getInvoiceDocument(fileId: string): Promise<Blob> {
    return this._coreService.delayResolve(new Blob());
  }

  getPayoutsByPartner(filters: PayoutListingFilters): Promise<Payout[]> {
    return this._coreService.delayResolve(
      this.payouts.filter((value, index) => {
        return (
          value.partnerId != undefined &&
          filters.partnerIds.indexOf(value.partnerId) > -1
        );
      }),
    );
  }

  async updatePayoutPreferences(
    payoutPreferencesRequest: PayoutPreferencesRequest,
  ): Promise<boolean> {
    return this._coreService.delayResolve(true);
  }

  getInvoices(filters: InvoicesListingFilters): Promise<Invoice[]> {
    return this._coreService.delayResolve(
      mockInvoices.filter((value, index) => {
        return (
          value.partnerId != undefined &&
          filters.partnerIds.indexOf(value.partnerId) > -1
        );
      }),
    );
  }

  createWithdrawalAgainstInvoices(
    request: WithdrawlAgainstInvoicesRequest,
  ): Promise<WithdrawlAgainstInvoicesResponse> {
    return this._coreService.delayResolve({
      status: true,
      payoutInitiatedDateUTC: '2025-01-31T00:00:01Z',
      payoutInitiatedCurrency: 'USD',
      payoutFinalCurrency: 'INR',
      payoutInitiatedAmount: 20000,
      payoutFinalAmount: 1600000,
      numberOfInvoicesPaid: 2,
    });
    // return this._coreService.delay({
    //   status: false,
    // });
  }
}
